import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import service from '../service';
import type { TicketedEvent } from '../types/ticketedEvents';
import type { SyncTicketTypesPayload } from '../types/api';

export interface SyncTicketTypesVariables {
  eventId: string;
  ticketTypes: SyncTicketTypesPayload['ticketTypes'];
}

export type SyncTicketTypesResult = TicketedEvent;

export function useSyncTicketTypes(
  options?: UseMutationOptions<
    SyncTicketTypesResult,
    Error,
    SyncTicketTypesVariables
  >,
): UseMutationResult<SyncTicketTypesResult, Error, SyncTicketTypesVariables> {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<SyncTicketTypesResult, Error, SyncTicketTypesVariables>({
    mutationKey: ['ticketTypes', 'sync'],
    mutationFn: async ({ eventId, ticketTypes }) => {
      return service.syncTicketTypes(eventId, ticketTypes);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: ['ticketedEvent', variables.eventId],
      });
      queryClient.invalidateQueries({ queryKey: ['ticketed-events'] });
      queryClient.invalidateQueries({
        queryKey: ['ticketedEvent'],
        exact: false,
      });
      onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
